/**
 * Fare Type Mapping Utility
 * Maps API fare identifiers to user-friendly names with colors
 */

export const FARE_TYPE_MAP = {
  'SALE': {
    label: 'Sale',
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200'
  },
  'PUBLISHED': {
    label: 'Published',
    color: 'peach',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200'
  },
  'SME': {
    label: 'SME',
    color: 'orange',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-800',
    borderColor: 'border-orange-300'
  },
  'CORPORATE': {
    label: 'Published',
    color: 'green',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200'
  },
  'FLEXI': {
    label: 'Flexi',
    color: 'peach',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200'
  },
  'COUPON': {
    label: 'Coupon',
    color: 'peach',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200'
  },
  'FAMILY': {
    label: 'Family',
    color: 'peach',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200'
  },
  'TACTICAL': {
    label: 'Tactical',
    color: 'peach',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200'
  },
  'PREMIUM_FLEX': {
    label: 'Premium Flex',
    color: 'peach',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200'
  },
  'GOMORE': {
    label: 'Gomore',
    color: 'peach',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200'
  },
  'CORPORATE_FLEX': {
    label: 'Published',
    color: 'green',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-green-200'
  },
  'FLEXI_PLUS': {
    label: 'Flexi Plus',
    color: 'peach',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200'
  },
  'SPECIAL_RETURN': {
    label: 'SPECIAL RETURN',
    color: 'peach',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200'
  },
  'OFFER_FARE_WITHOUT_PNR': {
    label: 'Offer Fare',
    color: 'pink',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-700',
    borderColor: 'border-pink-200'
  },
  'OFFER_FARE_WITH_PNR': {
    label: 'Instant Offer Fare',
    color: 'pink',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-700',
    borderColor: 'border-pink-200'
  },
  'SOTO_Light': {
    label: 'Light',
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200'
  },
  'SOTO_LIGHT': {
    label: 'Light',
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200'
  },
  'SOTO_OTA_VALUE_PLUS': {
    label: 'Value Plus',
    color: 'purple',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200'
  },
  'SOTO_OTA Value Plus': {
    label: 'Value Plus',
    color: 'purple',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200'
  },
  'REGULAR_FARE': {
    label: 'Regular',
    color: 'gray',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200'
  },
  'INSTANT_OFFER_FARE': {
    label: 'Instant Offer',
    color: 'pink',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-700',
    borderColor: 'border-pink-200'
  }
};

/**
 * Get user-friendly fare type label
 * @param {string} apiFareType - API fare identifier
 * @returns {string} User-friendly label
 */
export const getFareTypeLabel = (apiFareType) => {
  if (!apiFareType) return 'Standard';
  
  // Normalize the fare type by removing spaces and converting to uppercase
  const normalizedType = apiFareType.replace(/\s+/g, '_').toUpperCase();
  const fareType = FARE_TYPE_MAP[normalizedType];
  return fareType ? fareType.label : apiFareType;
};

/**
 * Get fare type styling
 * @param {string} apiFareType - API fare identifier
 * @returns {object} Styling classes
 */
export const getFareTypeStyling = (apiFareType) => {
  if (!apiFareType) {
    return {
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-200'
    };
  }
  
  // Normalize the fare type by removing spaces and converting to uppercase
  const normalizedType = apiFareType.replace(/\s+/g, '_').toUpperCase();
  const fareType = FARE_TYPE_MAP[normalizedType];
  return fareType || {
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200'
  };
};

/**
 * Get complete fare type info
 * @param {string} apiFareType - API fare identifier
 * @returns {object} Complete fare type info with label and styling
 */
export const getFareTypeInfo = (apiFareType) => {
  const label = getFareTypeLabel(apiFareType);
  const styling = getFareTypeStyling(apiFareType);
  
  return {
    label,
    ...styling
  };
};
